/**
 * The two rules every loader in this app obeys.
 *
 * 1. **Don't flash.** A spinner that appears and vanishes inside 200ms reads as a
 *    glitch, not as feedback — worse than showing nothing. So a loader waits a
 *    beat before appearing, and once it has appeared it stays long enough to be
 *    read rather than snapping away mid-blink.
 * 2. **Don't block.** Every wait is capped; a slow or dead widget must never hold
 *    up the rest of the page.
 *
 * Both hooks are timer-only — no state is written on the animation path, so
 * nothing here can cause a render storm.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface DeferredLoadingOptions {
  /** Nothing is shown for loads that finish inside this. */
  delay?: number;
  /** Once shown, the loader stays at least this long. */
  minDuration?: number;
}

/**
 * Debounces a boolean "is loading" flag into a boolean "should show a loader".
 *
 * A 90ms cache hit renders no loader at all; a 3s cold request shows one that
 * does not vanish the instant the response lands.
 */
export function useDeferredLoading(
  active: boolean,
  { delay = 180, minDuration = 420 }: DeferredLoadingOptions = {},
): boolean {
  const [visible, setVisible] = useState(false);
  const shownAt = useRef(0);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (active) {
      if (visible) return;
      timer = setTimeout(() => {
        shownAt.current = Date.now();
        setVisible(true);
      }, delay);
    } else if (visible) {
      const remaining = minDuration - (Date.now() - shownAt.current);
      if (remaining <= 0) {
        setVisible(false);
      } else {
        timer = setTimeout(() => setVisible(false), remaining);
      }
    }

    return () => clearTimeout(timer);
  }, [active, visible, delay, minDuration]);

  return visible;
}

export interface StagedLoadOptions {
  /**
   * How long a stage may hold up the ones behind it. A widget whose query hangs
   * releases the next stage anyway, so one bad endpoint cannot leave half the
   * page permanently empty.
   */
  watchdogMs?: number;
  /**
   * Minimum gap between stages. Small on purpose: enough for the browser to
   * paint the finished section before the next request lands, not enough to feel
   * like an artificial delay.
   */
  stepMs?: number;
  /** Skip staging entirely (e.g. a refetch, where everything is already warm). */
  enabled?: boolean;
}

export interface StagedLoad {
  /** True once stage `index` is allowed to start fetching. */
  reached: (index: number) => boolean;
  /** Call when stage `index` has settled — success or error, both release the gate. */
  settle: (index: number, settled: boolean) => void;
  /** Highest stage currently released. */
  current: number;
}

/**
 * Releases work in ordered stages: stage N starts only once stage N-1 has data
 * on screen.
 *
 * This is what makes the dashboard fill in — cards, then each chart, then the
 * tables below — instead of firing a dozen parallel requests that all land at
 * once after the slowest one. It also keeps the browser's connection pool free
 * for the request that is actually in front of the user.
 */
export function useStagedLoad(
  stageCount: number,
  { watchdogMs = 2500, stepMs = 90, enabled = true }: StagedLoadOptions = {},
): StagedLoad {
  const [current, setCurrent] = useState(() => (enabled ? 0 : stageCount - 1));
  const advanceTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Read inside the stable `settle` callback, so releasing a stage never has to
  // re-create the callback and re-fire every subscriber's effect.
  const currentRef = useRef(current);
  currentRef.current = current;

  // The watchdog is armed against whichever stage is currently in front.
  useEffect(() => {
    if (!enabled || current >= stageCount - 1) return;
    const timer = setTimeout(() => setCurrent((stage) => Math.max(stage, current + 1)), watchdogMs);
    return () => clearTimeout(timer);
  }, [current, stageCount, watchdogMs, enabled]);

  useEffect(() => () => clearTimeout(advanceTimer.current), []);

  const settle = useCallback(
    (index: number, settled: boolean) => {
      if (!settled || index !== currentRef.current || index >= stageCount - 1) return;
      clearTimeout(advanceTimer.current);
      advanceTimer.current = setTimeout(
        () => setCurrent((now) => Math.max(now, index + 1)),
        stepMs,
      );
    },
    [stageCount, stepMs],
  );

  return useMemo(
    () => ({
      current,
      settle,
      reached: (index: number) => !enabled || index <= current,
    }),
    [current, settle, enabled],
  );
}

/**
 * Fires `settle` for a stage as soon as its queries stop being pending.
 *
 * Kept separate from `useStagedLoad` so a section can decide for itself what
 * "done" means — the KPI row waits on two queries, a chart on one.
 */
export function useStageSettled(stage: StagedLoad, index: number, settled: boolean) {
  const { settle } = stage;
  useEffect(() => {
    settle(index, settled);
  }, [settle, index, settled]);
}
